import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { formatInTz } from "@/lib/format";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Loader2, RefreshCcw, Tag, Video } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

const CATEGORY_LABELS = { Vehicle: "Vehicul", Person: "Persoană", Animal: "Animal", Others: "Altele" };
const CATEGORY_VARIANTS = { Vehicle: "default", Person: "secondary", Animal: "outline", Others: "destructive" };

export default function EventsPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list() });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: () => api.cameras.list() });
  const [projectId, setProjectId] = useState("all");
  const [cameraId, setCameraId] = useState("all");
  const [category, setCategory] = useState("all");
  const [pendingClipId, setPendingClipId] = useState(null);

  const params = {
    projectId: projectId === "all" ? undefined : Number(projectId),
    cameraId: cameraId === "all" ? undefined : Number(cameraId),
    category: category === "all" ? undefined : category,
  };
  const { data: events, isLoading } = useQuery({ queryKey: ["events", params], queryFn: () => api.events.list(params) });

  const rerun = useMutation({
    mutationFn: (id) => api.clips.rerunAi(id),
    onMutate: (id) => setPendingClipId(id),
    onSuccess: () => { toast.success("Reanaliză AI declanșată. Evenimentele se vor actualiza în scurt timp."); qc.invalidateQueries({ queryKey: ["events"] }); },
    onError: (e) => toast.error(`Eșec rerun AI: ${apiError(e)}`),
    onSettled: () => setPendingClipId(null),
  });

  const projectsById = useMemo(() => { const m = new Map(); (projects || []).forEach((p) => m.set(p.id, p)); return m; }, [projects]);
  const camerasById = useMemo(() => { const m = new Map(); (cameras || []).forEach((c) => m.set(c.id, c.name)); return m; }, [cameras]);

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid="events-page">
        <header>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">Evenimente</h1>
          <p className="text-sm text-muted-foreground mt-1">Filtrare în stil Reolink: Vehicul, Persoană, Animal, Altele. Detecțiile pot fi adăugate manual sau de un model AI.</p>
        </header>

        <Card>
          <CardHeader><CardTitle className="text-base">Filtre</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="grid gap-1.5"><Label>Proiect</Label>
              <Select value={projectId} onValueChange={(v) => { setProjectId(v); setCameraId("all"); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toate</SelectItem>{projects?.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label>Cameră</Label>
              <Select value={cameraId} onValueChange={setCameraId} disabled={projectId === "all"}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toate</SelectItem>{cameras?.filter((c) => projectId === "all" || c.projectId === Number(projectId)).map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid gap-1.5"><Label>Categorie</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Toate categoriile</SelectItem><SelectItem value="Vehicle">Vehicul</SelectItem><SelectItem value="Person">Persoană</SelectItem><SelectItem value="Animal">Animal</SelectItem><SelectItem value="Others">Altele</SelectItem></SelectContent>
              </Select></div>
          </CardContent>
        </Card>

        {isLoading ? <Skeleton className="h-40" /> : !events?.length ? (
          <Card><CardContent className="py-12 flex flex-col items-center text-center gap-3"><Tag className="h-10 w-10 text-muted-foreground" /><p className="text-sm text-muted-foreground max-w-md">Nu există evenimente pentru filtrul curent. Detecțiile sunt populate la încărcare automată sau prin AI opțional.</p></CardContent></Card>
        ) : (
          <Card><CardContent className="p-0"><ul className="divide-y divide-border">
            {events.map((e) => {
              const project = projectsById.get(e.projectId);
              const camera = camerasById.get(e.cameraId) ?? `Cameră #${e.cameraId}`;
              const isRunning = pendingClipId === e.clipId;
              return (
                <li key={e.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-testid={`event-row-${e.id}`}>
                  <div className="min-w-0 flex items-center gap-3">
                    <Badge variant={CATEGORY_VARIANTS[e.category]}>{CATEGORY_LABELS[e.category]}</Badge>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{camera} · {project?.name ?? `Proiect #${e.projectId}`}</p>
                      <p className="text-xs text-muted-foreground truncate">{formatInTz(e.startedAt, project?.timezone)}{e.confidence != null && <>{" · "}<span className="font-medium">încredere {(e.confidence * 100).toFixed(0)}%</span></>}</p>
                      {e.notes && <p className="text-xs text-muted-foreground/80 truncate italic mt-0.5">{e.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {e.clipUrl && <a href={mediaUrl(e.clipUrl)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline"><Video className="h-3 w-3" /> Deschide clip</a>}
                    {e.source === "ai" ? <Badge variant="outline" className="gap-1"><Bot className="h-3 w-3" /> AI</Badge> : <Badge variant="outline">Manual</Badge>}
                    {isAdmin && e.clipId && <Button size="sm" variant="outline" disabled={isRunning} onClick={() => rerun.mutate(e.clipId)} className="bg-background">{isRunning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}Reanalizează</Button>}
                  </div>
                </li>
              );
            })}
          </ul></CardContent></Card>
        )}
      </div>
    </DashboardLayout>
  );
}
